import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import "@/App.css";
import { PageHeader } from "@/sections";
import { ExternalLink, Instagram, Facebook, Youtube, Music2 } from "lucide-react";

const DEFAULTS = {
  social_instagram_url: "https://www.instagram.com/auburndruid/",
  social_facebook_url: "https://www.facebook.com/auburndruid",
  social_youtube_url: "https://www.youtube.com/channel/UCTNuzp2eE3kM7hvEwmveFkQ",
  social_soundcloud_url: "https://soundcloud.com/auburn-temple-builder/tracks",
};

function buildSocials(c) {
  const get = (k) => (c?.[k] && c[k].trim()) || DEFAULTS[k];
  return [
    {
      name: "Instagram",
      url: get("social_instagram_url"),
      handle: "Reels & behind the scenes",
      description: "Short clips of new creations, sound demos, and behind-the-scenes from the Waitetuna workshop.",
      icon: Instagram,
      gradient: "linear-gradient(135deg, #F58529 0%, #DD2A7B 50%, #8134AF 100%)",
      cta: "Follow on Instagram",
    },
    {
      name: "Facebook",
      url: get("social_facebook_url"),
      handle: "@auburndruid",
      description: "Posts about new flutes, drums, and didgeridoos as they're finished. The easiest place to message me.",
      icon: Facebook,
      gradient: "#1877F2",
      cta: "Follow on Facebook",
    },
    {
      name: "YouTube",
      url: get("social_youtube_url"),
      handle: "Auburn Druid channel",
      description: "The making of the flutes, how-to-play tutorials, and instrument close-ups in beautiful detail.",
      icon: Youtube,
      gradient: "#FF0000",
      cta: "Subscribe on YouTube",
    },
    {
      name: "SoundCloud",
      url: get("social_soundcloud_url"),
      handle: "auburn-temple-builder",
      description: "Full-length didgeridoo tracks and Shamanic Power Animal Journey recordings — sit back and breathe.",
      icon: Music2,
      gradient: "#FF7700",
      cta: "Listen on SoundCloud",
    },
  ];
}

function SocialCard({ s }) {
  const Icon = s.icon;
  return (
    <a href={s.url} target="_blank" rel="noopener noreferrer"
      className="admin-card p-8 flex flex-col gap-4 hover:border-[var(--si-terracotta)] transition-all hover:-translate-y-1 group"
      data-testid={`connect-${s.name.toLowerCase()}`}>
      <div className="flex items-start justify-between">
        <div className="w-14 h-14 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: s.gradient }}>
          <Icon size={26} />
        </div>
        <ExternalLink size={16} className="text-[var(--si-text-2)] group-hover:text-[var(--si-terracotta)] mt-2" />
      </div>
      <div>
        <p className="label-eyebrow mb-1">{s.name}</p>
        <p className="font-display text-2xl md:text-3xl leading-tight mb-1">{s.handle}</p>
      </div>
      <p className="text-[var(--si-text-2)] text-sm md:text-base leading-relaxed flex-1">{s.description}</p>
      <span className="inline-flex items-center gap-2 text-sm font-medium text-[var(--si-terracotta)] mt-auto">
        {s.cta} →
      </span>
    </a>
  );
}

function InstagramReelEmbed({ url, title }) {
  const match = url?.match(/instagram\.com\/(?:reel|reels|p)\/([A-Za-z0-9_-]+)/);
  if (!match) return null;
  return (
    <div className="flex flex-col" data-testid="ig-embed">
      <div className="rounded-2xl overflow-hidden bg-black shadow-xl mx-auto w-full max-w-sm" style={{ aspectRatio: "9 / 16" }}>
        <iframe src={`https://www.instagram.com/reel/${match[1]}/embed`} className="w-full h-full" allow="encrypted-media" frameBorder="0" scrolling="no" title={title || "Instagram reel"} />
      </div>
      {title && <p className="text-center text-sm text-[var(--si-text-2)] mt-3">{title}</p>}
    </div>
  );
}

function YouTubeEmbed({ url, title }) {
  // Accept watch?v=ID, youtu.be/ID, or /embed/ID
  const m = url?.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([A-Za-z0-9_-]{6,})/);
  if (!m) return null;
  return (
    <div className="flex flex-col" data-testid="yt-embed">
      <div className="rounded-2xl overflow-hidden bg-black aspect-video shadow-xl">
        <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${m[1]}`} title={title || "YouTube video"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
      {title && <p className="text-center text-sm text-[var(--si-text-2)] mt-3">{title}</p>}
    </div>
  );
}

function FacebookEmbed({ url, title }) {
  if (!url) return null;
  const src = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`;
  return (
    <div className="flex flex-col" data-testid="fb-embed">
      <div className="rounded-2xl overflow-hidden bg-black aspect-video shadow-xl">
        <iframe className="w-full h-full" src={src} title={title || "Facebook video"} allow="encrypted-media" frameBorder="0" scrolling="no" allowFullScreen />
      </div>
      {title && <p className="text-center text-sm text-[var(--si-text-2)] mt-3">{title}</p>}
    </div>
  );
}

function MediaLinkEmbed({ link }) {
  if (link.platform === "instagram") return <InstagramReelEmbed url={link.url} title={link.title} />;
  if (link.platform === "youtube") return <YouTubeEmbed url={link.url} title={link.title} />;
  if (link.platform === "facebook") return <FacebookEmbed url={link.url} title={link.title} />;
  return null;
}

export default function ConnectPage() {
  const [content, setContent] = useState(null);
  const [mediaLinks, setMediaLinks] = useState([]);
  useEffect(() => {
    api.get("/site-content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/media-links").then((r) => setMediaLinks(r.data)).catch(() => {});
  }, []);

  const socials = buildSocials(content);
  const igReels = mediaLinks.filter((m) => m.platform === "instagram");
  const ytVideos = mediaLinks.filter((m) => m.platform === "youtube");
  const fbVideos = mediaLinks.filter((m) => m.platform === "facebook");

  return (
    <div className="App">
      <Nav artisanName={content?.artisan_name || "Sacred Instruments"} />
      <PageHeader
        eyebrow="Connect · Follow · Listen"
        title="Find me everywhere."
        subtitle="Follow along on social — new flutes, drums and didgeridoos as they're finished, sound demos, and the occasional Shamanic journey."
      />

      <section className="px-6 md:px-12 lg:px-24 pb-16">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-6 md:gap-8">
          {socials.map((s) => <SocialCard key={s.name} s={s} />)}
        </div>
      </section>

      {igReels.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 py-20 bg-[var(--si-bg-2)]/40" data-testid="featured-reels">
          <div className="max-w-7xl mx-auto">
            <p className="label-eyebrow mb-3 text-center">Featured Instagram reels</p>
            <h2 className="font-display text-3xl md:text-5xl font-light text-center mb-12">From the workshop</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {igReels.map((m) => <MediaLinkEmbed key={m.id} link={m} />)}
            </div>
          </div>
        </section>
      )}

      {ytVideos.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 py-20" data-testid="featured-youtube">
          <div className="max-w-7xl mx-auto">
            <p className="label-eyebrow mb-3">Featured videos</p>
            <h2 className="font-display text-3xl md:text-5xl font-light mb-12">Watch on YouTube</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {ytVideos.map((m) => <MediaLinkEmbed key={m.id} link={m} />)}
            </div>
          </div>
        </section>
      )}

      {fbVideos.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 py-20 bg-[var(--si-bg-2)]/40" data-testid="featured-facebook">
          <div className="max-w-7xl mx-auto">
            <p className="label-eyebrow mb-3">Featured on Facebook</p>
            <h2 className="font-display text-3xl md:text-5xl font-light mb-12">Facebook videos</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {fbVideos.map((m) => <MediaLinkEmbed key={m.id} link={m} />)}
            </div>
          </div>
        </section>
      )}

      <Footer content={content || {}} />
    </div>
  );
}
